import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Info, Shield } from "lucide-react";
import { ROLE_DESCRIPTION_MAX_LENGTH, ROLE_NAME_MAX_LENGTH } from "@nexus/contracts";
import { Button, Dialog, DialogContent, cn } from "@nexus/ui";
import {
  useAccessTemplates,
  useCreateAccessTemplate,
  usePreviewAccessTemplateCascade,
  useUpdateAccessTemplate,
} from "@/features/identity-hub/hooks/useIdentityHub";
import {
  type AccessTemplateDto,
  type CascadePreview,
  type TemplateScopeMode,
} from "@/features/identity-hub/api/identity-hub.api";
import { usePermissions, useIamPermission, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { OrgFilterCombo } from "./OrgFilterCombo";
import type { OrgPickerItem } from "./OrgPickerCombobox";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";
import { IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { ROLE_PERMISSION_DIALOG_CONTENT_CLASS } from "@/features/identity-hub/lib/permission-catalog";
import { affectedCascadeTenants } from "@/features/identity-hub/lib/cascade-preview";
import { TemplateCascadePreview } from "./TemplateCascadePreview";
import { TemplateScopeChoice } from "./TemplateScopeChoice";
import {
  permissionKeysToPayload,
  permissionPayloadToKeys,
  unmatchedPermissionTuples,
  usePermissionSelection,
  lockedCatalogKeys,
  repartitionPermissionDraft,
  type PermissionPayloadItem,
} from "@/features/identity-hub/hooks/usePermissionSelection";
import { useGrantableCatalogKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import { PermissionAccessCards, PermissionAccessFooter } from "./PermissionAccessCards";
import { roleDescriptionError, roleNameError } from "@/features/identity-hub/lib/role-field-errors";

const GRANTED_TO_CHIPS_SHOWN = 4;
/** The editor takes whatever the details leave, never under 320px; only its two columns scroll. */
const PERMISSIONS_EDITOR_SIZE_CLASS = "min-h-80 flex-1";
/** The open details panel scrolls itself rather than push the editor; short screens split the space. */
const PERMISSIONS_EDITOR_WITH_DETAILS_CLASS = "min-h-80 flex-1 [@media(max-height:560px)]:min-h-40";
/** A definite height gives the editor something to fill. Short screens get the whole viewport. */
const TEMPLATE_DIALOG_SIZE_CLASS =
  "h-[calc(100dvh-2rem)] max-h-[56rem] [@media(max-height:560px)]:h-dvh [@media(max-height:560px)]:max-h-dvh [@media(max-height:560px)]:rounded-none";
/** Tall enough for the open details panel plus the 320px editor, so details start expanded. */
const TALL_VIEWPORT_QUERY = "(min-height: 780px)";
/** DESIGN.md `drawer-right` width: the role editor has no org tab row to fit, unlike the 920px org drawer. */
const ROLE_EDITOR_DRAWER_MIN_WIDTH_PX = 720;
const SHORT_VIEWPORT_CHROME_CLASS = "[@media(max-height:560px)]:py-2.5";
/** On short screens the Name label sits beside its input, buying the editor its 320px. */
const SHORT_VIEWPORT_INLINE_LABEL_CLASS =
  "[@media(max-height:560px)]:flex-row [@media(max-height:560px)]:items-center [@media(max-height:560px)]:gap-3";

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgs: OrgPickerItem[];
  activeTenantId: string | null;
  editing: AccessTemplateDto | null;
  chrome?: "dialog" | "drawer";
}

export function TemplateDialog({
  open,
  onOpenChange,
  orgs,
  activeTenantId,
  editing,
  chrome = "dialog",
}: TemplateDialogProps) {
  const isEdit = editing != null;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isNameTouched, setIsNameTouched] = useState(false);
  const nameErrorId = useId();
  const descriptionErrorId = useId();
  const detailsHeadingId = useId();
  const detailsPanelId = useId();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [scopeMode, setScopeMode] = useState<TemplateScopeMode>("FIXED");
  const [editorOrgId, setEditorOrgId] = useState("");
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const { selected, setSelected, togglePerm, toggleGroup, removeKeys } = usePermissionSelection();
  const [preservedPermissions, setPreservedPermissions] = useState<PermissionPayloadItem[]>([]);
  const [preview, setPreview] = useState<CascadePreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const prevOpenRef = useRef(false);
  const selectedRef = useRef(selected);
  const preservedRef = useRef(preservedPermissions);
  selectedRef.current = selected;
  preservedRef.current = preservedPermissions;

  const { t } = useTranslation();
  const createTemplate = useCreateAccessTemplate();
  const updateTemplate = useUpdateAccessTemplate();
  const existingTemplates = useAccessTemplates({ includeArchived: true }, { enabled: open });
  const previewCascade = usePreviewAccessTemplateCascade();
  const perms = usePermissions();
  const hasCreate = useIamPermission(perms.IDENTITY_HUB_CREATE_TEMPLATE);
  const hasUpdate = useIamPermission(perms.IDENTITY_HUB_UPDATE_TEMPLATE);
  const hasPreviewPerm = useIamPermission(perms.IDENTITY_HUB_PREVIEW_TEMPLATE);
  const hasPreview = hasPreviewPerm || hasCreate || hasUpdate;
  const hasPermission = isEdit ? hasUpdate : hasCreate;
  const canCreateWildcard = useIsSuperTenant();
  const grantableKeys = useGrantableCatalogKeys();
  const lockedKeys = useMemo(
    () => (editing ? lockedCatalogKeys(editing.permissions, grantableKeys) : new Set<string>()),
    [editing, grantableKeys],
  );

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;
    setIsNameTouched(false);
    setIsDetailsOpen(prefersExpandedDetails());
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? "");
      setScopeMode(editing.scopeMode);
      setEditorOrgId(editing.canBeEditedByTenantId ?? editing.ownerTenant?.id ?? activeTenantId ?? "");
      setOrgIds(editing.tenantIds);
      setSelected(permissionPayloadToKeys(editing.permissions, grantableKeys));
      setPreservedPermissions(unmatchedPermissionTuples(editing.permissions, grantableKeys));
      setPreview(null);
    } else {
      setName("");
      setDescription("");
      setScopeMode("FIXED");
      setEditorOrgId(activeTenantId ?? orgs[0]?.id ?? "");
      setOrgIds(orgs[0]?.id ? [orgs[0].id] : []);
      setSelected(new Set());
      setPreservedPermissions([]);
      setPreview(null);
    }
  }, [open, editing, orgs, activeTenantId, grantableKeys, setSelected]);

  const grantableKeysRef = useRef(grantableKeys);
  useEffect(() => {
    const previousGrantableKeys = grantableKeysRef.current;
    grantableKeysRef.current = grantableKeys;
    if (!open || !editing || previousGrantableKeys === grantableKeys) return;
    const next = repartitionPermissionDraft(selectedRef.current, preservedRef.current, grantableKeys);
    setSelected(next.selected);
    setPreservedPermissions(next.preserved);
  }, [grantableKeys, open, editing, setSelected]);

  const trimmedName = name.trim();
  const nameError = roleNameError(t, name);
  const descriptionError = roleDescriptionError(t, description);
  const isNameTaken = isRoleNameTaken(existingTemplates.data ?? [], trimmedName, editing?.id);
  const nameProblem = nameError ?? (isNameTaken
    ? t("iam.validation.roleNameTaken", "A role with this name already exists")
    : null);
  const showNameError = nameProblem !== null && (isNameTouched || name.length > 0);
  const submitScope: TemplateScopeMode = canCreateWildcard ? scopeMode : "FIXED";
  const isWildcard = submitScope === "WILDCARD";
  // Fixed roles act in the organization that owns them ("Can be edited by"),
  // not in whichever grant target happens to sort first.
  const amberTenantId = isWildcard ? activeTenantId : (editorOrgId || orgIds[0] || activeTenantId);
  const draftPermissions = useMemo(
    () => [
      ...permissionKeysToPayload(selected),
      ...preservedPermissions,
    ],
    [selected, preservedPermissions],
  );
  const needsPreview = useMemo(() => {
    if (isWildcard) return false;
    const permKey = (p: { type: string; method: string; route: string }) =>
      `${p.type}|${p.method}|${p.route}`;
    const samePerms = (a: Array<{ type: string; method: string; route: string }>, b: typeof a) => {
      if (a.length !== b.length) return false;
      const keys = new Set(a.map(permKey));
      return b.every((p) => keys.has(permKey(p)));
    };
    const sameTenants = (a: string[], b: string[]) => {
      if (a.length !== b.length) return false;
      const set = new Set(a);
      return b.every((id) => set.has(id));
    };
    if (isEdit && editing) {
      return !samePerms(editing.permissions, draftPermissions)
        || !sameTenants(editing.tenantIds, orgIds);
    }
    return true;
  }, [isWildcard, isEdit, editing, draftPermissions, orgIds]);
  const previewBlocked = needsPreview && !preview && !hasPreview;
  const canSubmit =
    hasPermission &&
    nameProblem === null &&
    descriptionError === null &&
    !!editorOrgId &&
    !!amberTenantId &&
    (isWildcard || orgIds.length > 0) &&
    !previewBlocked &&
    !createTemplate.isPending &&
    !updateTemplate.isPending &&
    !previewCascade.isPending &&
    !isPreviewing;

  const handleOpenChange = (v: boolean) => {
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !amberTenantId) return;
    const permissions = draftPermissions;

    const save = async () => {
      if (isEdit && editing) {
        await updateTemplate.mutateAsync({
          templateId: editing.id,
          tenantId: amberTenantId,
          changes: {
            name: trimmedName,
            description: description.trim() || null,
            tenantIds: isWildcard ? undefined : orgIds,
            permissions,
          },
        });
      } else {
        await createTemplate.mutateAsync({
          name: trimmedName,
          description: description.trim() || undefined,
          scopeMode: submitScope,
          tenantIds: isWildcard ? [] : orgIds,
          permissions,
          tenantId: amberTenantId,
          canBeEditedByTenantId: editorOrgId,
        });
      }
      handleOpenChange(false);
    };

    if (needsPreview && !preview) {
      setIsPreviewing(true);
      try {
        const next = await previewCascade.mutateAsync({
          templateId: editing?.id,
          tenantId: amberTenantId,
          name: trimmedName,
          scopeMode: submitScope,
          tenantIds: isWildcard ? [] : orgIds,
          permissions,
        });
        setPreview(next);
      } catch {
        // usePreviewAccessTemplateCascade.onError reports the failure; keep the dialog open.
      } finally {
        setIsPreviewing(false);
      }
      return;
    }

    try {
      await save();
    } catch {
      // useAmberMutation reports save failures; keep the dialog open.
    }
  };

  const ownerName = orgs.find((org) => org.id === editorOrgId)?.name;
  const detailsSummary = [
    isWildcard
      ? t("iam.templates.dialog.scopeWildcardTitle", "Any organization")
      : t("iam.templates.dialog.organizationsCount", { count: orgIds.length, defaultValue: "{{count}} organizations" }),
    ownerName ? t("iam.templates.dialog.detailsManagedBy", "Managed by {{name}}", { name: ownerName }) : null,
  ].filter(Boolean).join(" · ");

  const isPending = createTemplate.isPending || updateTemplate.isPending || previewCascade.isPending || isPreviewing;

  const editor = (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogIconHeader
          icon={Shield}
          tone="primary"
          className={cn("pr-14 [@media(max-height:560px)]:[&_p]:hidden", SHORT_VIEWPORT_CHROME_CLASS)}
          title={preview
            ? t("iam.templates.dialog.previewTitle", "Review changes before saving")
            : isEdit
              ? t("iam.templates.dialog.editTitle", "Edit role")
              : t("iam.templates.dialog.createTitle", "New role")}
          description={preview
            ? t("iam.templates.dialog.previewStep", "{{name}} · Step 2 of 2", { name: trimmedName })
            : isEdit
              ? t("iam.templates.dialog.editDescription", "Update this catalog role. Scope cannot change after create.")
              : t("iam.templates.dialog.createDescription", "Define a catalog role and the organizations it covers.")}
        />

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-3 px-6 py-3 [@media(max-height:560px)]:gap-2 [@media(max-height:560px)]:py-2",
            preview ? "overflow-y-auto" : "overflow-hidden",
          )}
        >
          {preview ? (
            <TemplateCascadePreview preview={preview} orgs={orgs} />
          ) : (
          <>
          <section className="flex min-h-0 flex-col gap-2" aria-labelledby={detailsHeadingId}>
            <h3 id={detailsHeadingId} className="sr-only">
              {t("iam.templates.dialog.detailsHeading", "Role details")}
            </h3>
            <div className="flex shrink-0 flex-wrap items-end gap-x-3 gap-y-2">
              <label className={cn("flex min-w-48 flex-1 flex-col gap-1.5", SHORT_VIEWPORT_INLINE_LABEL_CLASS)}>
                <FieldLabel
                  label={t("iam.templates.dialog.nameLabel", "Name")}
                  marker={<span className="text-destructive" aria-hidden>*</span>}
                  count={name.trim().length}
                  max={ROLE_NAME_MAX_LENGTH}
                />
                <input
                  value={name}
                  dir="auto"
                  required
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setIsNameTouched(true)}
                  placeholder={t("iam.templates.dialog.namePlaceholder", "e.g. Editor")}
                  aria-invalid={showNameError || undefined}
                  aria-describedby={showNameError ? nameErrorId : undefined}
                  className={cn(
                    "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-1",
                    showNameError
                      ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                      : "border-input-border focus:border-ring focus:ring-ring/40",
                  )}
                />
              </label>
              <button
                type="button"
                aria-expanded={isDetailsOpen}
                aria-controls={detailsPanelId}
                onClick={() => setIsDetailsOpen((open) => !open)}
                className="flex h-9 max-w-full items-center gap-2 rounded-md border border-input-border bg-background px-3 text-xs outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <span className="font-medium text-foreground">
                  {t("iam.templates.dialog.detailsHeading", "Role details")}
                </span>
                <span className="min-w-0 truncate text-muted-foreground">{detailsSummary}</span>
                <ChevronDown
                  className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", isDetailsOpen && "rotate-180")}
                  aria-hidden
                />
              </button>
            </div>
            {showNameError && (
              <span id={nameErrorId} className="shrink-0 break-words text-[11px] text-destructive">
                {nameProblem}
              </span>
            )}
            {isDetailsOpen && (
              <div
                id={detailsPanelId}
                className="grid min-h-0 grid-cols-1 content-start gap-3 overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-muted/40 p-3 sm:grid-cols-2"
              >
                {canCreateWildcard && (
                  <div className="sm:col-span-2">
                    <TemplateScopeChoice value={scopeMode} onValueChange={setScopeMode} locked={isEdit} />
                  </div>
                )}
                {isWildcard ? (
                  <p className="self-center text-xs text-muted-foreground">
                    {t("iam.templates.dialog.wildcardHint", "This role applies to any grantable organization at grant time.")}
                  </p>
                ) : (
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium text-foreground">
                        {t("iam.templates.dialog.organizationsLabel", "Granted to")}
                      </span>
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {t("iam.templates.dialog.organizationsCount", { count: orgIds.length, defaultValue: "{{count}} organizations" })}
                      </span>
                    </span>
                    <OrgFilterCombo
                      mode="multi"
                      variant="field"
                      items={orgs}
                      value={orgIds}
                      onValueChange={setOrgIds}
                      maxShown={GRANTED_TO_CHIPS_SHOWN}
                      label={t("iam.templates.dialog.organizationsLabel", "Granted to")}
                      searchPlaceholder={t("iam.templates.dialog.organizationsSearchPlaceholder", "Search organizations…")}
                      ariaLabel={t("iam.templates.dialog.organizationsAria", "Granted to")}
                    />
                  </div>
                )}
                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground">
                    {t("iam.templates.dialog.managedByHeading", "Managed by")}
                  </span>
                  <OrgFilterCombo
                    mode="single"
                    variant="field"
                    items={orgs}
                    value={editorOrgId}
                    onValueChange={setEditorOrgId}
                    disabled={isEdit}
                    disabledReason={t("iam.templates.dialog.managedByLocked", "Can't be changed after the role is created")}
                    label={t("iam.templates.dialog.managedByHeading", "Managed by")}
                    searchPlaceholder={t("iam.templates.dialog.canBeEditedBySearchPlaceholder", "Search organizations…")}
                    ariaLabel={t("iam.templates.dialog.managedByHeading", "Managed by")}
                  />
                </div>
                <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                  <FieldLabel
                    label={t("iam.templates.dialog.descriptionLabel", "Description")}
                    marker={
                      <span className="font-normal text-muted-foreground">
                        {t("iam.templates.dialog.optional", "(optional)")}
                      </span>
                    }
                    count={description.trim().length}
                    max={ROLE_DESCRIPTION_MAX_LENGTH}
                  />
                  <textarea
                    value={description}
                    dir="auto"
                    rows={2}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("iam.templates.dialog.descriptionPlaceholder", "Optional description")}
                    aria-invalid={descriptionError ? true : undefined}
                    aria-describedby={descriptionError ? descriptionErrorId : undefined}
                    className={cn(
                      "w-full resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1",
                      descriptionError
                        ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                        : "border-input-border focus:border-ring focus:ring-ring/40",
                    )}
                  />
                  {descriptionError && (
                    <span id={descriptionErrorId} className="break-words text-[11px] text-destructive">
                      {descriptionError}
                    </span>
                  )}
                </label>
              </div>
            )}
          </section>

          {previewBlocked && (
            <p className="shrink-0 text-sm text-destructive" role="alert">
              {t(
                "iam.templates.dialog.previewPermissionRequired",
                "You need Preview role save permission to confirm organization changes before saving.",
              )}
            </p>
          )}
          <PermissionAccessCards
            className={isDetailsOpen ? PERMISSIONS_EDITOR_WITH_DETAILS_CLASS : PERMISSIONS_EDITOR_SIZE_CLASS}
            selected={selected}
            onTogglePerm={togglePerm}
            onToggleGroup={toggleGroup}
            onRemoveKeys={removeKeys}
            showSelectedOnly={false}
            grantableKeys={grantableKeys}
            lockedKeys={lockedKeys}
          />
        </>
          )}
        </div>

        <div className={cn("flex items-center justify-between border-t border-border px-6 py-4", SHORT_VIEWPORT_CHROME_CLASS)}>
          {preview ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" aria-hidden />
              {t("iam.templates.dialog.previewNothingYet", "Nothing changes until you save.")}
            </span>
          ) : (
            <PermissionAccessFooter selected={selected} grantableKeys={grantableKeys} lockedKeys={lockedKeys} />
          )}
          <div className="flex items-center gap-2">
            {preview ? (
              <Button variant="outline" size="sm" iconStart="arrow-left" onClick={() => setPreview(null)}>
                {t("iam.templates.dialog.previewBack", "Back to edit")}
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
                {t("common.cancel")}
              </Button>
            )}
            <Button
              size="sm"
              iconStart="shield"
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
              data-testid={preview ? "template-cascade-confirm" : undefined}
            >
              {isPending
                ? (preview
                  ? t("iam.templates.dialog.saving", "Saving…")
                  : (isEdit
                    ? t("iam.templates.dialog.saving", "Saving…")
                    : t("iam.templates.dialog.creating", "Creating…")))
                : preview
                  ? t("iam.templates.dialog.previewConfirmCount", {
                      count: affectedCascadeTenants(preview).length,
                      defaultValue: "Save and update {{count}} organizations",
                    })
                  : (isEdit
                    ? t("iam.templates.dialog.save", "Save changes")
                    : t("iam.templates.dialog.create", "Create role"))}
            </Button>
          </div>
        </div>
    </div>
  );

  if (chrome === "drawer") {
    return (
      <IamOverlayDrawer
        open={open}
        onOpenChange={handleOpenChange}
        title={isEdit
          ? t("iam.templates.dialog.editTitle", "Edit role")
          : t("iam.templates.dialog.createTitle", "New role")}
        showClose
        minWidthPx={ROLE_EDITOR_DRAWER_MIN_WIDTH_PX}
      >
        {editor}
      </IamOverlayDrawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={cn(ROLE_PERMISSION_DIALOG_CONTENT_CLASS, TEMPLATE_DIALOG_SIZE_CLASS)} {...PREVENT_DISMISS_PROPS}>
        {editor}
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ label, marker, count, max }: { label: string; marker: ReactNode; count: number; max: number }) {
  return (
    <span className="flex shrink-0 items-baseline justify-between gap-2 whitespace-nowrap">
      <span className="text-xs font-medium text-foreground">
        {label} {marker}
      </span>
      <span
        aria-hidden
        className={cn("text-[11px] tabular-nums", count > max ? "text-destructive" : "text-muted-foreground")}
      >
        {count} / {max}
      </span>
    </span>
  );
}

function prefersExpandedDetails(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(TALL_VIEWPORT_QUERY).matches;
}

function isRoleNameTaken(roles: AccessTemplateDto[], trimmedName: string, editingId: string | undefined): boolean {
  if (!trimmedName) return false;
  const wanted = trimmedName.toLocaleLowerCase();
  return roles.some((role) => role.id !== editingId && role.name.trim().toLocaleLowerCase() === wanted);
}
